import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Megaphone, Play, Pause, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const { refreshUser } = useAuth();

  const load = async () => {
    const { data } = await api.get('/campaigns');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const launch = async (id) => {
    try {
      await api.post(`/campaigns/${id}/launch`);
      toast.success('Campaign is now live. 50 ZAP used.');
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pause = async (id) => {
    try { await api.post(`/campaigns/${id}/pause`); toast.success('Paused'); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await api.delete(`/campaigns/${id}`); toast.success('Deleted'); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-7" data-testid="campaigns-root">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Campaigns</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Your active programs</h1>
        </div>
        <Link to="/campaigns/new" className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4"/> New campaign</Link>
      </div>

      {items.length === 0 ? (
        <div className="glass rounded-2xl py-20 text-center">
          <Megaphone className="w-12 h-12 mx-auto mb-4 text-white/20"/>
          <div className="text-white/55 mb-5">No campaigns yet.</div>
          <Link to="/campaigns/new" className="btn-primary rounded-full px-5 py-3 inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Build your first</Link>
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 text-[10px] tracking-[0.2em] uppercase text-white/40 font-bold px-6 py-4 border-b border-white/5">
            <div className="col-span-4">Campaign</div>
            <div className="col-span-2">Platform</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Performance</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {items.map(c => (
            <div key={c.campaign_id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-white/5 hover:bg-white/[0.02] transition" data-testid={`campaign-row-${c.campaign_id}`}>
              <div className="col-span-4">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-white/40">{c.objective} · £{c.budget}</div>
              </div>
              <div className="col-span-2 text-sm text-white/65 uppercase tracking-wide">{c.platform}</div>
              <div className="col-span-2">
                <span className={`text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold ${c.status==='live'?'bg-boosta-emerald/15 text-boosta-emerald border border-boosta-emerald/30':c.status==='paused'?'bg-boosta-yellow/15 text-boosta-yellow border border-boosta-yellow/30':'bg-white/5 text-white/50 border border-white/10'}`}>{c.status}</span>
              </div>
              <div className="col-span-2 text-right">
                <div className="font-bold">£{(c.spend??0).toFixed(2)}</div>
                <div className="text-xs text-white/45">{c.conversions ?? 0} conv · {c.roas ?? 0}x</div>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                {c.status !== 'live' ? (
                  <button onClick={()=>launch(c.campaign_id)} title="Launch (50 ZAP)" className="p-2 rounded-lg bg-boosta-yellow/15 text-boosta-yellow hover:bg-boosta-yellow/25 inline-flex items-center gap-1 text-xs" data-testid={`launch-${c.campaign_id}`}><Play className="w-3.5 h-3.5"/><Zap className="w-3 h-3"/>50</button>
                ) : (
                  <button onClick={()=>pause(c.campaign_id)} title="Pause" className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid={`pause-${c.campaign_id}`}><Pause className="w-3.5 h-3.5"/></button>
                )}
                <button onClick={()=>del(c.campaign_id)} title="Delete" className="p-2 rounded-lg bg-white/5 hover:bg-red-500/15 hover:text-red-400" data-testid={`delete-${c.campaign_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
